// Pure helpers for the invoice form's Property & Building sections. Kept out of
// InvoiceFormModal.jsx so they can be unit-tested without dragging the modal's
// PDF previewer into Jest.
import { resolveRateType, flatAmountOf } from "./Api";

export const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const DEFAULT_GST_RATE = 5;

// Mirrors MAX_INVOICE_FILES on the server.
export const MAX_INVOICE_FILES = 10;

/**
 * The invoice files of a group: the keys every line carries. The server copies
 * the invoice files onto every line, so anything only some lines hold is that
 * line's own row file. Mirrors sharedAttachmentKeys on the server.
 */
export const sharedAttachmentKeys = (items = []) =>
  items
    .map((item) => new Set(item?.attachments || []))
    .reduce((acc, keys) => (acc === null ? Array.from(keys) : acc.filter((k) => keys.has(k))), null) || [];

// Shown when a submission has no invoice file and no row file. The file is
// required, so the toast says so plainly instead of "fix the highlighted fields".
export const INVOICE_FILE_REQUIRED_MESSAGE =
  "Invoice file is required — attach a PDF, photo or screenshot of your invoice before submitting.";

// Validation keys per form card, in the order the cards appear on screen.
const SECTION_ERROR_KEYS = [
  ["details", ["title", "vendorId", "categoryId"]],
  ["lines", ["lines"]],
  ["file", ["attachments"]],
];

/** The first card (top to bottom) holding a validation error, or null. */
export const firstInvalidSection = (errors = {}) => {
  const hit = SECTION_ERROR_KEYS.find(([, keys]) => keys.some((k) => errors[k]));
  return hit ? hit[0] : null;
};

/** The toast for a failed submit: the first card's actual problem. */
export const submitErrorMessage = (errors = {}) => {
  const section = firstInvalidSection(errors);
  if (section === "file") return INVOICE_FILE_REQUIRED_MESSAGE;
  if (!section) return "Please fix the highlighted fields";
  const [, keys] = SECTION_ERROR_KEYS.find(([name]) => name === section);
  return errors[keys.find((k) => errors[k])];
};

/**
 * Every building the picker may offer, grouped by property. Accepts both the
 * `building: [{buildingName}]` shape (internal property list) and the
 * `buildings: ["A", "B"]` shape (vendor eligibility list).
 */
export const buildingChoicesByProperty = (properties = []) =>
  (properties || [])
    .map((p) => {
      const raw = Array.isArray(p.buildings) && p.buildings.length
        ? p.buildings.map((b) => (typeof b === "string" ? b : b?.buildingName))
        : (p.building || []).map((b) => b?.buildingName);
      return {
        propertyId: p._id,
        label: p.title || p.name || p._id,
        buildings: Array.from(new Set(raw.filter(Boolean))),
      };
    })
    .filter((p) => p.buildings.length)
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

// ── Sections covering several buildings ───────────────────────────────────
// A section is one property. It normally names one building (`buildingName`);
// when several of the property's buildings are picked together they sit in
// `buildingNames` instead, and each row then names its own building. Every line
// sent to the server still belongs to exactly one building.

/** The buildings a section covers, in the order picked. */
export const sectionBuildings = (section) =>
  Array.isArray(section?.buildingNames) && section.buildingNames.length
    ? section.buildingNames
    : section?.buildingName ? [section.buildingName] : [];

/** The building a row bills: its own pick in a several-building section, else the section's. */
export const rowBuildingName = (section, row) => {
  const list = sectionBuildings(section);
  return String((list.length > 1 ? row?.buildingName : list[0]) || "").trim();
};

/**
 * The section with its building list replaced. One building (or none) is kept in
 * `buildingName`; several go to `buildingNames`, and a row whose building was
 * dropped is cleared — except that rows of a section that had one building keep
 * it when that building is still picked.
 */
export const withSectionBuildings = (section, names = []) => {
  const list = Array.from(new Set((names || []).filter(Boolean)));
  if (list.length <= 1) return { ...section, buildingName: list[0] || "", buildingNames: [] };
  const prev = sectionBuildings(section);
  const carry = prev.length === 1 && list.includes(prev[0]) ? prev[0] : "";
  return {
    ...section,
    buildingName: "",
    buildingNames: list,
    rows: (section.rows || []).map((r) => (list.includes(r.buildingName) ? r : { ...r, buildingName: carry })),
  };
};

/**
 * Lay the buildings picked in the "Multiple buildings" checklist out as form
 * sections, one section per property. Picks of the property the picker was
 * opened from join that section; picks of a property that already has a section
 * join it; the section the picker was opened from, when it has no building yet
 * and none of its property was picked, takes the first other property instead
 * of being left empty; any remaining property gets a new section at the end. A
 * building that is already on the form is skipped.
 */
export const addBuildingSections = (groups, gi, picks, makeGroup) => {
  const has = new Set(groups.flatMap((g) => sectionBuildings(g).map((b) => `${g.propertyId}::${b}`)));
  const fresh = picks.filter((p) => !has.has(`${p.propertyId}::${p.buildingName}`));
  if (!fresh.length) return groups;
  const byProperty = new Map();
  fresh.forEach((p) => {
    const pid = String(p.propertyId);
    if (!byProperty.has(pid)) byProperty.set(pid, []);
    byProperty.get(pid).push(p.buildingName);
  });
  const next = [...groups];
  const target = next[gi];
  let targetFree = !!target && !sectionBuildings(target).length && !byProperty.has(String(target.propertyId));
  byProperty.forEach((names, pid) => {
    let i = -1;
    if (target && String(target.propertyId) === pid) i = gi;
    if (i < 0) i = next.findIndex((g) => String(g.propertyId) === pid && sectionBuildings(g).length);
    if (i < 0 && targetFree) i = gi;
    if (i === gi) targetFree = false;
    if (i >= 0) next[i] = withSectionBuildings({ ...next[i], propertyId: pid }, [...sectionBuildings(next[i]), ...names]);
    else next.push(withSectionBuildings(makeGroup(pid, ""), names));
  });
  return next;
};

/**
 * Rebuild the form sections from an invoice's line items.
 *
 * An invoice is stored as one document per line, but it was ENTERED as property
 * sections each holding unit rows. Regrouping the lines by property + building
 * and laying their rows back out shows the invoice exactly the way it was
 * created. Both View and Edit use this.
 *
 * Sections and rows both keep the order the items arrive in, which the server
 * has already resolved from lineIndex (falling back to creation order for
 * invoices raised before batching).
 *
 * `editable` decides whether a row pins itself to the amount on record:
 *   - View (default) keeps `storedAmount`, so a line priced before the current
 *     fields existed still shows the real charge instead of a re-derived $0.
 *   - Edit must NOT carry it. rowSubtotal() prefers storedAmount whenever it is
 *     present, so an editable row that kept it would show the old figure no
 *     matter what the user typed — the totals would never move.
 */
export const groupsFromItems = (items = [], { editable = false } = {}) => {
  const map = new Map();
  // A line's own files are whatever it holds beyond the shared invoice files.
  // They are shown on the row as saved (read-only) chips.
  const invoiceFiles = new Set(sharedAttachmentKeys(items));
  items.forEach((item) => {
    const ref = (Array.isArray(item?.properties) ? item.properties : [])[0] || item?.property || {};
    const propertyId = ref.propertyId ? String(ref.propertyId) : "";
    const buildingName = ref.buildingName || "";
    // Every line carries its own rate type; a section is purely property + building.
    const rateType = resolveRateType(item);
    const key = `${propertyId}::${buildingName}`;
    if (!map.has(key)) {
      // `title` is resolved server-side by enrichInvoices, so the View can name
      // the property even when the viewer's own property list does not contain
      // it — an auditor's list is empty, and a Select would render blank.
      map.set(key, {
        id: uid(),
        propertyId,
        buildingName,
        propertyLabel: ref.title || item?.propertyTitle || "",
        rows: [],
      });
    }
    const row = {
      id: uid(),
      // The line this row was built from. Edit mode sends it back as the line's
      // `_id` so the server updates that document; the read-only View never
      // uses it.
      itemId: item?._id,
      rateType,
      unitNo: ref.unitNo || "",
      jobDescription: ref.jobDescription || "",
      hours: item?.hours ?? "",
      minutes: item?.minutes ?? "",
      amountPerHour: item?.amountPerHour ?? "",
      flatAmount: rateType === "flat" ? flatAmountOf(item) : "",
      materialCost: item?.materialCost ?? "",
      gstRate: item?.gstRate ?? DEFAULT_GST_RATE,
      comment: item?.comment || "",
      files: (item?.attachments || [])
        .filter((key) => !invoiceFiles.has(key))
        .map((key) => ({ id: key, key, name: key.split("/").pop(), signedUrl: null, saved: true })),
    };
    if (!editable) {
      // What the invoice actually records, so a flat-priced line is not
      // re-derived to zero from absent hours and rate.
      row.storedAmount = Number.isFinite(Number(item?.amount)) ? Number(item.amount) : undefined;
    }
    map.get(key).rows.push(row);
  });
  return Array.from(map.values());
};

/**
 * The `lines` array for PUT /invoice/group/:groupId/lines, straight from the
 * form sections. A row that came from the server carries `itemId` and is sent
 * with `_id` so the server updates that document; a row added in the form has
 * none and is created. Only the live pricing field set is sent, mirroring the
 * create payload, so a flat line can never carry a stale hourly rate.
 */
export const linesPayloadFromGroups = (groups = []) =>
  groups.flatMap((group) =>
    (group.rows || []).map((row) => {
      const rateType = row.rateType || "hourly";
      return {
        ...(row.itemId ? { _id: row.itemId } : {}),
        propertyId: group.propertyId,
        buildingName: rowBuildingName(group, row),
        unitNo: (row.unitNo || "").trim(),
        jobDescription: (row.jobDescription || "").trim(),
        rateType,
        ...(rateType === "flat"
          ? { flatAmount: Number(row.flatAmount) }
          : {
              hours: Number(row.hours) || 0,
              minutes: Number(row.minutes) || 0,
              amountPerHour: Number(row.amountPerHour),
            }),
        materialCost: Number(row.materialCost) || 0,
        gstRate: Number(row.gstRate),
        comment: (row.comment || "").trim(),
      };
    })
  );

// ── Property (billing) groups ─────────────────────────────────────────────
// A vendor's group names the buildings one invoice bills together. While a
// group is chosen, the form only offers those buildings; every line still
// belongs to one building, so nothing downstream changes.

const normName = (v) => String(v == null ? "" : v).trim().toLowerCase();

/**
 * The form's property list cut down to a group's buildings. Keeps the shape it
 * was given: `buildings: ["A"]` (vendor eligibility) or `building: [{buildingName}]`
 * (internal property list). With no group the list is returned unchanged.
 */
export const restrictPropertiesToGroup = (properties = [], group) => {
  if (!group) return properties;
  const allowed = new Map();
  (group.buildings || []).forEach((b) => {
    const pid = String(b.propertyId);
    if (!allowed.has(pid)) allowed.set(pid, new Set());
    allowed.get(pid).add(normName(b.buildingName));
  });
  return (properties || [])
    .filter((p) => allowed.has(String(p._id)))
    .map((p) => {
      const keep = allowed.get(String(p._id));
      if (Array.isArray(p.buildings) && p.buildings.length) {
        return { ...p, buildings: p.buildings.filter((b) => keep.has(normName(typeof b === "string" ? b : b?.buildingName))) };
      }
      return { ...p, building: (p.building || []).filter((b) => keep.has(normName(b?.buildingName))) };
    });
};

/** The section's buildings that the group does not hold ([] when none, or no group). */
export const buildingsOutsideGroup = (section, group) =>
  !group || !section?.propertyId
    ? []
    : sectionBuildings(section).filter((name) => String(name).trim() && !(group.buildings || []).some(
      (b) => String(b.propertyId) === String(section.propertyId) && normName(b.buildingName) === normName(name)
    ));

/** False only when a section names a building the group does not hold. */
export const sectionInGroup = (section, group) => !buildingsOutsideGroup(section, group).length;

/** The group's buildings as BuildingPicker picks ({ propertyId, buildingName }). */
const rowHasEntry = (r = {}) =>
  ["unitNo", "jobDescription", "hours", "minutes", "amountPerHour", "flatAmount", "materialCost", "comment"]
    .some((k) => String(r[k] == null ? "" : r[k]).trim() !== "") ||
  (r.files || []).length > 0 ||
  !!r.itemId;

/**
 * The form sections after switching to another property group. Sections on the
 * new group's buildings stay; a several-building section loses the buildings the
 * group doesn't hold. One left with no building is dropped when nothing was typed
 * in it, or keeps its rows with the property and building cleared so they can be
 * re-picked. Always leaves at least one section.
 */
export const sectionsForGroup = (groups = [], group, makeGroup) => {
  if (!group) return groups;
  const next = [];
  (groups || []).forEach((g) => {
    const outside = buildingsOutsideGroup(g, group);
    const kept = sectionBuildings(g).filter((b) => !outside.includes(b));
    if (!outside.length) next.push(g);
    else if (kept.length) next.push(withSectionBuildings(g, kept));
    else if ((g.rows || []).some(rowHasEntry)) next.push({ ...withSectionBuildings(g, []), propertyId: "" });
  });
  return next.length ? next : [makeGroup()];
};

/** A group's buildings gathered under their property names, for display. */
export const groupBuildingsByProperty = (group, properties = []) => {
  const map = new Map();
  (group?.buildings || []).forEach((b) => {
    const pid = String(b.propertyId);
    if (!map.has(pid)) {
      const p = (properties || []).find((x) => String(x._id) === pid);
      map.set(pid, { propertyId: pid, title: (p && (p.title || p.name)) || "Property", buildings: [] });
    }
    map.get(pid).buildings.push(b.buildingName);
  });
  return Array.from(map.values());
};

export const groupBuildingPicks = (group) =>
  (group?.buildings || []).map((b) => ({ propertyId: String(b.propertyId), buildingName: b.buildingName }));

/** The company that owns the first property chosen on the form ("" when none is known). */
export const companyOfSections = (properties = [], sections = []) => {
  for (const s of sections || []) {
    if (!s?.propertyId) continue;
    const p = (properties || []).find((x) => String(x._id) === String(s.propertyId));
    const c = p && (p.companyId || p.companyID);
    if (c) return String(c?._id || c);
  }
  return "";
};
